// Knowledge Base Content - All Languages
// Loads the appropriate v4 content based on current language

import { KB_CONTENT_EN } from './knowledgeBase/en.js';

// Japanese KB Content (v4.0)
export const KB_CONTENT_JA = `# AIRLINE OPS: ナレッジベース
## プレイヤーガイド v4.0

---

# ようこそ

Airline Opsは、路線を構築し、機体を増やし、新しいハブへ拡大し、エリートオペレーターステータスを目指す航空会社経営シミュレーターです。

**タイマーなし。エナジーなし。放置ゲームの要素なし。**

あなたと、あなたの航空会社、そして素晴らしいものを築き上げる挑戦だけがここにあります。

---

# 目次

1. はじめに
2. 最初の1日
3. 路線の構築
4. 機体の拡大
5. 路線需要
6. 評判とティア
7. 関係性とハブ招待
8. ハブオークション
9. ハブストア
10. 航空機メンテナンス
11. 天候
12. 時間加速
13. ウィング
14. エリートオペレーターステータス (EOS)
15. ゴールドA380
16. コレクタブル航空機
17. ヒントと戦略

---

# はじめに

## スタート時の状態

ゲーム開始時の所持品：
- **$40,000,000** の現金
- **2,000,000ガロン** の燃料（満タン）
- **航空機ゼロ** — 購入が必要
- **KPDX（ポートランド国際空港）** がホームハブ
- 世界中の **4,878空港** にアクセス可能

## やるべきこと

1. **航空機を購入** — 手頃なリージョナル機から始める
2. **路線を作成** — ハブと他の空港を結ぶ
3. **フライトを飛ばす** — DEPART ALLをクリックして全機発進
4. **収益を得る** — 完了したフライトから収入が発生
5. **拡大** — より多くの航空機、路線、ハブへ
6. **エリートオペレーターステータスを達成** — 究極の目標

---

# 最初の1日

## ステップ1：最初の航空機を購入

**航空機マーケット**へ行きます。$650Kから数億ドルまでの機体があります。

**おすすめのスターター機：**
| 航空機 | 価格 | 乗客数 | 理由 |
|--------|------|--------|------|
| DHC-2 | $650K | 7 | 最安、学習に最適 |
| DHC-3 | $850K | 11 | やや多い輸送力 |
| Beech-99 | $1.2M | 15 | 最高のコスパ |

最初は3〜5機購入しましょう。全額使い切らず、予備を残しておくこと。

## ステップ2：路線を作成

**Routes**に行き、**+ New Route**をクリック。

ハブ（KPDX）から目的地を選びます。近くの空港がおすすめ：
- KSEA（シアトル） — 130nm
- KBOI（ボイジー） — 298nm
- KRDM（レドモンド） — 101nm

航空機を路線に割り当てます。

## ステップ3：フライトを開始

**DEPART ALL**をクリックして、準備完了の全航空機を発進させます。

マップ上で飛行を確認できます。着陸すると自動的に折り返して戻ります。完了したフライトごとに収益が蓄積されます。

---

# 路線の構築

## 路線パネル

各路線には以下が表示されます：
- **出発地 → 目的地**（例：KPDX → KSEA）
- **距離**（海里）
- **割り当てられた航空機**とそのステータス
- **需要インジケーター**（色付きドット）
- **収益**予測

## 路線ごとに1カード

KPDX → KSEAに4機飛ばしている場合、4枚の別々のカードではなく、4機すべてを表示する1枚のカードが表示されます。

路線カードの**LAUNCH ALL**をクリックすると、その路線の準備完了の全航空機を発進させます。

## 新規路線の作成

1. **+ New Route**をクリック
2. 出発地を選択（自分のハブまたは所有する別のハブ）
3. 目的地を選択（4,878空港から任意）
4. 航空機を割り当て
5. 路線が作成され、飛行準備完了

---

# 機体の拡大

## 航空機は購入のみ

リースはありません。運航する航空機はすべて所有です。つまり：
- 大きな初期費用
- 本当の財務判断
- 機体は資産

## 機数が重要

より多くの航空機 = より多くの路線 = より多くの収益 = より速い成長

ただし、無理は禁物。以下のために現金を確保：
- メンテナンス（A-Check）
- ハブオークション入札
- 予期せぬ機会

## 機体のアップグレード

成長に応じて、より大きな航空機へ：
- **リージョナル**（E175、CRJ-900） — 70-90人
- **ナローボディ**（737、A320） — 150-180人
- **ワイドボディ**（787、A350） — 250-350人
- **ジャンボ**（747、A380） — 400人以上

大型機 + 長距離路線 = フライトあたりの収益増加

---

# 路線需要

## 需要の仕組み

すべての路線には0-100%の**需要レベル**があります。

- **高需要（80-100%）**：満額収益
- **中需要（50-79%）**：収益減少
- **低需要（30-49%）**：大幅に収益減少
- **枯渇（30%未満）**：最低収益

## 利用で需要が減少

完了したフライトごとに需要が1.5%減少。

1つの路線に10機投入すると、需要は急速に低下します。

## 時間経過で需要が回復

ゲーム内の1日ごとに、需要は3%回復します（最大100%まで）。

酷使した路線を休ませれば、回復します。

## 視覚的インジケーター

マップ上の路線ラインは需要に応じて色が変わります：
- **緑** — 高需要、満額収益
- **黄** — 中需要
- **オレンジ** — 低需要
- **赤** — 枯渇、低収益

**戦略**：路線を分散させましょう。1つの路線に全機投入しないこと。

---

# 評判とティア

## 評判とは？

評判はあなたの航空会社のグローバルな地位です。乗客からの評価と、どんな機会がアンロックされるかに影響します。

## 5つのティア

| ティア | 意味 |
|--------|------|
| ティア1 | 新規航空会社、無名 |
| ティア2 | 確立されたリージョナル |
| ティア3 | 成長中のネットワーク |
| ティア4 | 主要プレイヤー |
| ティア5 | エリートステータス対象 |

## 評判の構築方法

- **定時運航率（OTP）** — スケジュール通りに着陸
- **キャンセルゼロ** — 1便もキャンセルしない
- **一貫したサービス** — 飛び続け、成長し続ける
- **機数** — より多くの航空機は安定性を示す

## 評判が重要な理由

- 上位ティアでプレミアム料金がアンロック
- **ティア5はエリートオペレーターステータスに必須**

---

# 関係性とハブ招待

## 関係性とは？

関係性は**特定の空港**との関係です。評判（グローバル）とは異なり、関係性は空港ごとです。

## 関係性の構築方法

空港に継続的に飛行します。定時到着のたびに、その空港との関係が構築されます。

十分なフライト後、空港があなたに気づき、そこでハブになるための入札に招待される可能性があります。

## 関係性 ≠ 評判

以下のような状況がありえます：
- グローバルな評判は高いが、デンバーとの関係性はゼロ
- シアトルとの関係性は強いが、グローバルな評判は低い

異なることを追跡する別々のシステムです。

---

# ハブオークション

## ハブ招待の仕組み

空港との関係性が十分になると、**オークション招待**を受け取る可能性があります。

その空港でのハブステータスを入札するチャンスです。

## オークションプロセス

1. **カードを選ぶ（1-8）** — 入札順序が決まりますが、どの順番かはわかりません
2. **順番に入札が公開** — 自分の番では前の入札を見られます
3. **入札する** — いくら提示するか決める
4. **最高入札者が勝利** — 勝者がハブを獲得

## オークション戦略

- 早い順番の場合：見えない状態で入札。他者の入札額を予測
- 遅い順番の場合：他者の入札を見られる。最高額を上回ればいい
- カードの引きは運。入札額は戦略。

## AI競合相手

AIの航空会社と競います。彼らには予算があり、無限に入札しません。彼らの予算を超えれば、パスします。

## 初回ハブ — 2回のチャンス

最初のハブオークションは寛大です：
- 負けても、7日後に2回目のチャンス
- 2回負けると、関係性を構築して招待を獲得する必要あり

## 敗北後のクールダウン

オークションに負けると、その空港から再度招待を受けるまで7ゲーム日待つ必要があります。

---

# ハブストア

## オークションをスキップ

オークションで賭けたくない？**ハブストア**でハブを直接購入できます。

**注意点**：オークション価格より35%高いプレミアムを支払います。

| ハブ | ストア価格 |
|------|-----------|
| SFO | $60M |
| LAX | $60M |
| JFK | $70M |
| DEN | $70M |
| ORD | $75M |
| ATL | $75M |
| LHR | $80M |

## ハブストアを使うタイミング

- オークションで負け続けている
- 特定のハブが今すぐ必要
- 現金が余っている
- 招待を待ちたくない

ストアは便利さ。オークションは機会。

---

# 航空機メンテナンス

## コンディションの低下

フライトごとに航空機は摩耗します。完了したフライトごとにコンディションが0.2%低下。

## コンディション閾値

| コンディション | ステータス | 効果 |
|----------------|------------|------|
| 80-100% | 良好 | 通常運航 |
| 60-79% | 普通 | 10%の遅延確率 |
| 40-59% | 不良 | 25%の遅延確率、収益-10% |
| 40%未満 | 運航停止 | 飛行不可 |

## A-Checkメンテナンス

コンディションが低下したら、**A-Check**を実施して100%に復元。

| 航空機タイプ | A-Check費用 |
|--------------|-------------|
| リージョナル | $50,000 |
| ナローボディ | $150,000 |
| ワイドボディ | $400,000 |
| ジャンボ | $750,000 |

メンテナンス費用を予算に入れること。運航停止の機体は収益を生みません。

---

# 天候

## リアルタイム天候

天候は運航に影響します：
- **VFR** — 晴天、通常運航
- **MVFR** — 視界不良、一部制限
- **IFR** — 低視界、遅延の可能性
- **LIFR** — 非常に低い視界、大幅な遅延

## 天候レーダー

マップで天候レーダーを切り替えて、ネットワーク全体の状況を確認。

嵐を避けて計画を立てましょう。遅延は定時運航率に影響します。

---

# 時間加速

## ゲームをスピードアップ

リアルタイムで待ちたくない？ゲームを加速できます：

| 速度 | リアルタイム = ゲーム時間 |
|------|--------------------------|
| 1x | 1分 = 1分 |
| 2x | 1分 = 2分 |
| 4x | 1分 = 4分 (EOSのみ) |

速度は全プレイヤーで2xに制限されています。**EOS（エリートオペレーターステータス）**を獲得すると4x速度が解放されます。

---

# ウィング

## ウィングとは？

ウィングは、手動で1つずつ路線を作成する手間をかけずに、路線ネットワークを急速に拡大できる**便利通貨**です。

## ウィングの仕組み

**1ウィング**を使用すると、ゲームが自動的に：
1. ハブから最も近い、まだサービスしていない5つの空港を探す
2. それらの空港への路線を作成
3. 利用可能な航空機を路線に割り当て

メニューをクリックする必要なし。目的地を探す必要なし。即座に拡大。

## ウィングの入手方法

ウィングは**キャッシュストア**の購入に含まれています。キャッシュパックを購入すると、ゲーム内通貨と一緒にボーナスとしてウィングを受け取ります。

| パック | 含まれるウィング |
|--------|-----------------|
| スターターパック | 1ウィング |
| リージョナルパック | 3ウィング |
| エクスパンションパック | 5ウィング |
| タイクーンパック | 10ウィング |

## ウィングを使うタイミング

ウィングは以下に最適：
- **急速な初期拡大** — ネットワークを素早く成長
- **古い路線の置き換え** — 需要が枯渇した路線を一掃し、新鮮な路線を見つける
- **楽な管理** — 路線選択をゲームに任せる
- **新しい地域のテスト** — ハブ近くの空港を素早く確認

## ウィング vs 手動路線作成

| 手動路線 | ウィング |
|----------|----------|
| 目的地を自分で選択 | ゲームが最寄りの未就航地を選択 |
| 完全なコントロール | 便利さ |
| 時間がかかる | 即座 |
| 無料 | 5路線につき1ウィング |

ウィングはショートカットであり、代替ではありません。戦略的なプレイヤーは今後もプレミアム路線を厳選するでしょう。ウィングは大量拡大を楽にするだけです。

---

# エリートオペレーターステータス (EOS)

## EOSとは？

エリートオペレーターステータスは、Airline Opsでの最高の達成です。ワールドクラスの運航を構築し維持したことを証明します。

EOSは与えられるものではありません。獲得するものです。

## 要件

**14日連続**で以下を維持する必要があります：

| 要件 | 基準 |
|------|------|
| 評判 | ティア5 |
| 定時運航率 | 85%以上 |
| キャンセル | ゼロ |
| キャッシュフロー | 毎日プラス |
| 機数 | 30機以上 |

## 14日間のストリーク

ここからが本番です。

これらの数字を1回達成するだけでは足りません。**14日間連続で毎日**達成する必要があります。

- 1日目：全要件達成
- 2日目：全要件達成
- 3日目：全要件達成
- ...
- 13日目：全要件達成
- 14日目：全要件達成 → **EOS達成**

## ストリークが途切れる条件

**以下のいずれか**でカウンターがゼロにリセットされます：

- OTPが85%を下回る
- 1便でもキャンセル
- 1日でもキャッシュフローがマイナス
- 評判がティア5を下回る
- 機数が30機を下回る

13日目で1便キャンセル？**1日目からやり直し。**

天候、機械的問題、不運 — 何でもストリークを終わらせます。だからこそEOSには意味があるのです。

## EOSは永久

頑張りへの報酬：**一度EOSを獲得すれば、永久に保持。**

取り消されることはありません。自分を証明しました。あなたのものです。

## EOSでアンロックされるもの

EOS達成で以下にアクセス：

1. **EOSストア** — 限定マーケットプレイス
2. **ゴールドA380** — $375,000,000の航空機
3. **コレクタブルロール** — ゴールドA380購入ごとにランダムなコレクタブル航空機

ここからが本当のエンドゲームの始まりです。

---

# ゴールドA380

## 通常のA380ではない

ゴールドA380は、航空機マーケットの標準A380とは異なります。

- **EOSストアでのみ入手可能**
- **EOS達成後にのみ購入可能**
- **価格：$375,000,000**

## なぜ購入するのか？

ゴールドA380を購入するたびに、**コレクタブルロール**が発動。

飛行機を買うのではありません。レアなものを手に入れるチャンスを買うのです。

## ロール

ゴールドA380を購入すると：
1. ゲームがコレクタブルテーブルに対してロール
2. ランダムなコレクタブル航空機を受け取る
3. 航空機がコレクションに追加

$3億7500万は大金に聞こえます。実際そうです。だからこそEOS達成とコレクタブルロールがエンドゲームの追求なのです。

---

# コレクタブル航空機

## 134のコレクタブル

134種類のユニークなコレクタブル航空機があり、5つのレアリティティアに分かれています。

## ティア

| ティア | ドロップ率 | 内容 |
|--------|-----------|------|
| **レア** | 79.5% | プレミアムバリアント、軍用輸送機 |
| **レリック** | 10% | 歴史的な航空伝説 |
| **アノマラス** | 5% | 異常な特性を持つ奇妙な航空機 |
| **レジェンダリー** | 5% | エンジニアリングの驚異、中止されたプロジェクト |
| **ミシック** | 0.1% | 1機のみ存在。詳細不明。 |

## レア（79.5%）

ほとんどのロールはレアになります。プレミアム航空機バリアント、軍用輸送機、特別版です。コレクションへの堅実な追加ですが、よく出ます。

## レリック（10%）

航空黄金時代の歴史的航空機：
- スピリット・オブ・セントルイス
- スプルース・グース
- コンコルド
- ライトフライヤー
- その他

博物館の展示品です。所有することには意味があります。

## アノマラス（5%）

ここから奇妙になります。

アノマラス航空機は通常のルールに従いません。例：

| 航空機 | 効果 |
|--------|------|
| ザ・バミューダン | すべてのフライトがバミューダにリダイレクト |
| スプルース・ムース | 浴槽で1秒間飛行 |
| クォンタム・ホッパー | 瞬間移動（99,999ノット） |
| パッセンジャー・ゼロ | ゾンビ黙示録ニュースシーケンスを発動 |
| ヌル・ポインター | 偽のブルースクリーンを引き起こす |
| ザ・パーペチュアル | 強制するまで着陸しない |

これらは単なるコレクタブルではありません。何かをします。

## レジェンダリー（5%）

頂点のエンジニアリングと中止された夢：
- ボーイング・ソニッククルーザー（未製造）
- 747X（提案されたが中止）
- ボーイング2707 SST（アメリカの実現しなかった超音速機）

これらは「もしも」を表しています。

## ミシック（0.1%）

1機の航空機。0.1%の確率。

それが何かは教えません。何をするかも教えません。

自分で見つけてください。

## 重複

すでに持っているものが出た？重複はリロールに変換可能：
- レアの重複5つ = 1リロール
- レリックの重複3つ = 1リロール
- アノマラスの重複2つ = 1リロール

無駄になるものはありません。

---

# ヒントと戦略

## アーリーゲーム

1. **使いすぎない** — 安い航空機を3〜5機購入、現金を残す
2. **リージョナルから始める** — 短距離路線、素早いターンアラウンド、安定した収入
3. **OTPに注意** — 定時運航率は初日から重要
4. **路線を分散** — 1つの路線を使い潰さない

## ミッドゲーム

1. **関係性を構築** — ハブにしたい空港に継続的に飛行
2. **ハブオークションに勝つ** — ネットワークを拡大
3. **機体をアップグレード** — より大きな航空機へ
4. **需要を監視** — 路線を緑に保つ、赤にしない

## レイトゲーム

1. **ティア5を目指す** — EOSに必要
2. **85%以上のOTPを維持** — 譲れない
3. **予備を蓄積** — ゴールドA380ロールには$3億7500万必要
4. **ストリークに備える** — 14日は長い

## EOSへの挑戦

EOS達成を試みる準備ができたら：

1. **まず全要件を確認** — 確実になるまで始めない
2. **スケジュールにバッファを** — 天候があなたを試す
3. **決してキャンセルしない** — 遅延はしても、キャンセルはしない
4. **毎日監視** — 毎日指標を確認
5. **13日目は冷静に** — あと1日。失敗しないこと。

---

# 最後に

これは何かを築き、それをうまく運営できることを証明するゲームです。

ショートカットはありません。難しい部分をスキップするための課金もありません。タイマーがあなたを引き留めることもありません。

あなたと、あなたの決断、そしてエリートオペレーターステータスとの間に立ちはだかる14日間のストリークだけです。

幸運を。空があなたを待っています。

---

*ナレッジベース v4.0 — 2026年1月*
`;

// Simplified Chinese KB Content (v4.0)
export const KB_CONTENT_ZH = `# AIRLINE OPS：知识库
## 玩家指南 v4.0

---

# 欢迎

Airline Ops是一款航空公司管理模拟器，您可以建立航线、扩展机队、拓展新枢纽，并朝着精英运营商地位迈进。

**没有计时器。没有体力值。没有挂机玩法。**

只有您、您的航空公司，以及建设伟大事业的挑战。

---

# 目录

1. 入门指南
2. 第一天
3. 建立航线
4. 扩展机队
5. 航线需求
6. 声望与等级
7. 关系与枢纽邀请
8. 枢纽拍卖
9. 枢纽商店
10. 飞机维护
11. 天气
12. 时间加速
13. 翅膀
14. 精英运营商地位 (EOS)
15. 金色A380
16. 收藏飞机
17. 技巧与策略

---

# 入门指南

## 起始状态

游戏开始时您拥有：
- **$40,000,000** 现金
- **2,000,000加仑** 燃油（满油）
- **零架飞机** — 需要购买
- **KPDX（波特兰国际机场）** 作为您的枢纽
- 可访问全球 **4,878个机场**

## 您需要做的

1. **购买飞机** — 从价格实惠的支线飞机开始
2. **创建航线** — 将您的枢纽与其他机场连接
3. **执行航班** — 点击"全部起飞"发射所有飞机
4. **赚取收入** — 完成的航班产生收入
5. **扩张** — 更多飞机、更多航线、更多枢纽
6. **达成精英运营商地位** — 终极目标

---

# 第一天

## 第一步：购买您的第一架飞机

前往**飞机市场**。您会看到从$650K到数亿美元的各种飞机。

**推荐入门机型：**
| 飞机 | 价格 | 载客量 | 原因 |
|------|------|--------|------|
| DHC-2 | $650K | 7 | 最便宜，适合学习 |
| DHC-3 | $850K | 11 | 载客量略大 |
| Beech-99 | $1.2M | 15 | 最佳性价比 |

首先购买3-5架飞机。不要花光所有钱——保留储备资金。

## 第二步：创建航线

前往**航线**并点击**+ 新航线**。

从您的枢纽（KPDX）选择目的地。附近的机场是不错的选择：
- KSEA（西雅图） — 130海里
- KBOI（博伊西） — 298海里
- KRDM（雷德蒙德） — 101海里

为航线分配一架飞机。

## 第三步：启动航班

点击**全部起飞**发射所有准备就绪的飞机。

在地图上观察它们飞行。着陆后，它们会自动返航飞回。每完成一次航班都会累积收入。

---

# 建立航线

## 航线面板

每条航线显示：
- **出发地 → 目的地**（例如：KPDX → KSEA）
- **距离**（海里）
- **已分配的飞机**及其状态
- **需求指示器**（彩色圆点）
- **收入**预估

## 每条航线一张卡片

如果您有4架飞机飞行KPDX → KSEA，您会看到一张显示全部4架的卡片，而不是4张单独的卡片。

点击航线卡片上的**全部起飞**可发射该航线上所有准备就绪的飞机。

## 创建新航线

1. 点击**+ 新航线**
2. 选择出发地（您的枢纽或您拥有的其他枢纽）
3. 选择目的地（4,878个机场中任选）
4. 分配一架飞机
5. 航线创建完成，准备飞行

---

# 扩展机队

## 飞机只能购买

没有租赁。您运营的每架飞机都是您拥有的。这意味着：
- 大额前期成本
- 真正的财务决策
- 您的机队是资产

## 机队规模很重要

更多飞机 = 更多航线 = 更多收入 = 更快增长。

但不要过度扩张。保留现金用于：
- 维护（A检）
- 枢纽拍卖竞标
- 意外机会

## 升级您的机队

随着发展，逐步升级到更大的飞机：
- **支线**（E175、CRJ-900） — 70-90名乘客
- **窄体**（737、A320） — 150-180名乘客
- **宽体**（787、A350） — 250-350名乘客
- **巨无霸**（747、A380） — 400+名乘客

更大的飞机 + 更长的航线 = 每次航班更高的收入。

---

# 航线需求

## 需求如何运作

每条航线都有0-100%的**需求水平**。

- **高需求（80-100%）**：全额收入
- **中等需求（50-79%）**：收入减少
- **低需求（30-49%）**：收入大幅减少
- **枯竭（低于30%）**：最低收入

## 使用会消耗需求

每完成一次航班，需求降低1.5%。

如果您在一条航线上投入10架飞机，需求会快速下降。

## 需求随时间恢复

每个游戏日，需求恢复3%（最高100%）。

让过度使用的航线休息，它们会恢复。

## 视觉指示器

地图上的航线根据需求变色：
- **绿色** — 高需求，全额收入
- **黄色** — 中等需求
- **橙色** — 低需求
- **红色** — 枯竭，收入差

**策略**：分散您的航线。不要把所有飞机放在一条航线上。

---

# 声望与等级

## 什么是声望？

声望是您航空公司的全球地位。它影响乘客对您的看法以及您能解锁什么机会。

## 5个等级

| 等级 | 含义 |
|------|------|
| 等级1 | 新航空公司，无人知晓 |
| 等级2 | 成熟的支线航空 |
| 等级3 | 成长中的网络 |
| 等级4 | 主要玩家 |
| 等级5 | 有资格获得精英地位 |

## 如何建立声望

- **准点率（OTP）** — 按时着陆
- **零取消** — 从不取消航班
- **持续服务** — 持续飞行，持续增长
- **机队规模** — 更多飞机显示稳定性

## 声望为何重要

- 更高等级解锁高级定价
- **等级5是精英运营商地位的必要条件**

---

# 关系与枢纽邀请

## 什么是关系？

关系是您与**特定机场**的关系。与声望（全球性的）不同，关系是针对每个机场的。

## 如何建立关系

持续飞往某个机场。每次准点到达都会建立您与该机场的关系。

飞行足够多次后，机场会注意到您，并可能邀请您竞标成为那里的枢纽。

## 关系 ≠ 声望

您可能会有：
- 全球声望很高但与丹佛的关系为零
- 与西雅图的关系很强但全球声望很低

它们是跟踪不同内容的独立系统。

---

# 枢纽拍卖

## 枢纽邀请如何运作

当您与某个机场建立足够的关系后，您可能会收到**拍卖邀请**。

这是您竞标该机场枢纽地位的机会。

## 拍卖流程

1. **选择一张卡片（1-8）** — 这决定了您的出价顺序，但您不知道自己抽到哪个位置
2. **按顺序揭示出价** — 轮到您时可以看到之前的出价
3. **出价** — 决定要出多少
4. **最高出价者获胜** — 胜者获得枢纽

## 拍卖策略

- 如果您抽到靠前的位置：您在盲目出价。猜测其他人可能出多少。
- 如果您抽到靠后的位置：您能看到其他人的出价。只需超过最高价即可。
- 抽卡是运气。出价金额是策略。

## AI竞争对手

您在与AI航空公司竞争。他们有预算——不会无限出价。如果您出价超过他们的承受能力，他们会放弃。

## 首个枢纽 — 两次机会

您的第一次枢纽拍卖比较宽容：
- 如果输了，7天后会有第二次机会
- 如果输两次，您需要通过建立关系来赚取邀请

## 失败后的冷却期

如果您输掉拍卖，必须等待7个游戏日才能再次收到该机场的邀请。

---

# 枢纽商店

## 跳过拍卖

不想在拍卖中赌博？**枢纽商店**让您直接购买枢纽。

**代价是**：您需要支付比拍卖价格高35%的溢价。

| 枢纽 | 商店价格 |
|------|----------|
| SFO | $60M |
| LAX | $60M |
| JFK | $70M |
| DEN | $70M |
| ORD | $75M |
| ATL | $75M |
| LHR | $80M |

## 何时使用枢纽商店

- 您不断输掉拍卖
- 您现在就需要某个特定枢纽
- 您有多余的现金
- 您不想等待邀请

商店是便利。拍卖是机会。

---

# 飞机维护

## 状况会下降

每次飞行都会磨损您的飞机。每完成一次航班，状况下降0.2%。

## 状况阈值

| 状况 | 状态 | 效果 |
|------|------|------|
| 80-100% | 良好 | 正常运行 |
| 60-79% | 一般 | 10%延误概率 |
| 40-59% | 差 | 25%延误概率，收入-10% |
| 低于40% | 停飞 | 无法飞行 |

## A检维护

当状况变低时，执行**A检**将其恢复到100%。

| 飞机类型 | A检费用 |
|----------|---------|
| 支线 | $50,000 |
| 窄体 | $150,000 |
| 宽体 | $400,000 |
| 巨无霸 | $750,000 |

为维护做预算。停飞的机队不赚钱。

---

# 天气

## 实时天气

天气影响您的运营：
- **VFR** — 晴空，正常运营
- **MVFR** — 能见度边缘，一些限制
- **IFR** — 低能见度，可能延误
- **LIFR** — 极低能见度，严重延误

## 天气雷达

在地图上切换天气雷达，查看整个网络的状况。

绕过风暴制定计划。延误会影响您的准点率。

---

# 时间加速

## 加速游戏

不想实时等待？加速游戏：

| 速度 | 现实时间 = 游戏时间 |
|------|---------------------|
| 1x | 1分钟 = 1分钟 |
| 2x | 1分钟 = 2分钟 |
| 4x | 1分钟 = 4分钟 (仅限EOS) |

所有玩家的速度上限为2倍。获得**EOS（精英运营者身份）**可解锁4倍速度。

---

# 翅膀

## 什么是翅膀？

翅膀是一种**便利货币**，让您无需手动逐条创建航线就能快速扩展航线网络。

## 翅膀如何运作

当您花费**1个翅膀**时，游戏会自动：
1. 找到距离您枢纽最近的5个您尚未服务的机场
2. 创建通往这些机场的航线
3. 将可用飞机分配到这些航线

无需点击菜单。无需搜索目的地。即时扩展。

## 获取翅膀

翅膀包含在**现金商店**购买中。当您购买现金包时，除了游戏内货币外还会获得翅膀作为奖励。

| 礼包 | 包含翅膀 |
|------|----------|
| 入门礼包 | 1个翅膀 |
| 区域礼包 | 3个翅膀 |
| 扩展礼包 | 5个翅膀 |
| 大亨礼包 | 10个翅膀 |

## 何时使用翅膀

翅膀非常适合：
- **快速早期扩张** — 让您的网络快速增长
- **替换陈旧航线** — 清除需求枯竭的航线，找到新鲜航线
- **懒人管理** — 让游戏为您选择航线
- **测试新领域** — 快速查看枢纽附近有哪些机场

## 翅膀 vs 手动创建航线

| 手动航线 | 翅膀 |
|----------|------|
| 您选择目的地 | 游戏选择最近的未服务机场 |
| 完全控制 | 便利 |
| 耗时 | 即时 |
| 免费 | 每5条航线花费1个翅膀 |

翅膀是捷径，不是替代品。战略玩家仍然会精选优质航线。翅膀只是让大规模扩张变得轻松。

---

# 精英运营商地位 (EOS)

## 什么是EOS？

精英运营商地位是Airline Ops中的最高成就。它证明您已经建立并维护了世界级的运营。

EOS不是赠予的。是赢得的。

## 要求

**连续14天**，您必须保持：

| 要求 | 门槛 |
|------|------|
| 声望 | 等级5 |
| 准点率 | 85%或更高 |
| 取消 | 零 |
| 现金流 | 每天为正 |
| 机队规模 | 30架以上 |

## 14天连胜

这就是真正考验的开始。

您不只是需要达到这些数字一次。您需要**连续14天每天**都达到。

- 第1天：所有要求达成
- 第2天：所有要求达成
- 第3天：所有要求达成
- ...
- 第13天：所有要求达成
- 第14天：所有要求达成 → **EOS达成**

## 什么会中断您的连胜

**以下任何一项**都会将您的计数器重置为零：

- OTP降至85%以下
- 取消一个航班
- 一天现金流为负
- 声望降至等级5以下
- 机队规模降至30架以下

第13天取消了一个航班？**从第1天重新开始。**

天气、机械问题、运气不好——任何事都可能结束您的连胜。这就是EOS有意义的原因。

## EOS是永久的

这是努力的回报：**一旦获得EOS，您将永远保持。**

不会被撤销。您证明了自己。它属于您。

## EOS解锁什么

获得EOS后可以访问：

1. **EOS商店** — 独家市场
2. **金色A380** — $375,000,000的飞机
3. **收藏抽奖** — 每次购买金色A380都会给您一架随机收藏飞机

这就是真正的终局游戏开始的地方。

---

# 金色A380

## 不是普通的A380

金色A380与飞机市场的标准A380不同。

- **仅在EOS商店有售**
- **仅在获得EOS后可购买**
- **价格：$375,000,000**

## 为什么要买？

每次购买金色A380都会触发**收藏抽奖**。

您买的不是飞机。您买的是获得稀有物品的机会。

## 抽奖

当您购买金色A380时：
1. 游戏对收藏表进行抽奖
2. 您获得一架随机收藏飞机
3. 飞机加入您的收藏

3.75亿美元听起来很多。确实很多。这就是为什么达到EOS并抽取收藏品是终局游戏的追求。

---

# 收藏飞机

## 134种收藏品

有134种独特的收藏飞机，分为5个稀有度等级。

## 等级

| 等级 | 掉落率 | 内容 |
|------|--------|------|
| **稀有** | 79.5% | 高级变体、军用运输机 |
| **遗物** | 10% | 航空史上的传奇 |
| **异常** | 5% | 具有异常特性的奇怪飞机 |
| **传奇** | 5% | 工程奇迹、取消的项目 |
| **神话** | 0.1% | 仅存在1架。详情未知。 |

## 稀有（79.5%）

大多数抽奖结果是稀有。这些是高级飞机变体、军用运输机和特别版。是收藏的坚实补充，但很常见。

## 遗物（10%）

航空黄金时代的历史飞机：
- 圣路易斯精神号
- 云杉鹅
- 协和式
- 莱特飞行器
- 等等

这些是博物馆藏品。拥有它们意味着什么。

## 异常（5%）

从这里开始变得奇怪。

异常飞机不遵循正常规则。例如：

| 飞机 | 效果 |
|------|------|
| 百慕大者 | 每次飞行都重定向到百慕大 |
| 云杉驼鹿 | 在您的浴缸里飞行1秒 |
| 量子跳跃者 | 瞬间传送（99,999节） |
| 零号乘客 | 触发僵尸末日新闻序列 |
| 空指针 | 导致假蓝屏死机 |
| 永恒者 | 永不着陆，除非您强制 |

这些不只是收藏品。它们会做事情。

## 传奇（5%）

巅峰工程和取消的梦想：
- 波音音速巡航者（从未建造）
- 747X（提议但取消）
- 波音2707 SST（美国未实现的超音速飞机）

这些代表着"如果"。

## 神话（0.1%）

一架飞机。0.1%的概率。

我们不会告诉您它是什么。我们不会告诉您它做什么。

自己找到它。

## 重复

抽到已经拥有的？重复可以转换为重新抽奖：
- 5个稀有重复 = 1次重抽
- 3个遗物重复 = 1次重抽
- 2个异常重复 = 1次重抽

没有东西会浪费。

---

# 技巧与策略

## 早期游戏

1. **不要超支** — 买3-5架便宜飞机，保留现金储备
2. **从区域开始** — 短航线、快速周转、稳定收入
3. **关注OTP** — 准点率从第一天就很重要
4. **分散航线** — 不要把一条航线用到枯竭

## 中期游戏

1. **建立关系** — 持续飞往您想要作为枢纽的机场
2. **赢得枢纽拍卖** — 扩展您的网络
3. **升级机队** — 逐步升级到更大的飞机
4. **监控需求** — 保持航线绿色，而不是红色

## 后期游戏

1. **冲击等级5** — EOS需要它
2. **保持85%+ OTP** — 不可商量
3. **建立储备** — 每次金色A380抽奖需要$3.75亿
4. **为连胜做准备** — 14天是很长的时间

## EOS冲刺

当您准备尝试EOS时：

1. **先检查所有要求** — 确定之前不要开始
2. **在时间表中建立缓冲** — 天气会考验您
3. **永远不要取消** — 可以延误，但永远不要取消
4. **每天监控** — 每天检查您的指标
5. **第13天保持冷静** — 还有一天。不要搞砸。

---

# 最后说明

这是一款关于建设和证明您能够经营好的游戏。

没有捷径。没有付费跳过困难部分。没有计时器阻碍您。

只有您、您的决定，以及横亘在您和精英运营商地位之间的14天连胜。

祝您好运。天空在等待。

---

*知识库 v4.0 — 2026年1月*
`;

// Re-export English content
export { KB_CONTENT_EN };

// Get content by language code
export function getKBContent(language) {
  switch (language) {
    case 'ja':
      return KB_CONTENT_JA;
    case 'zh-CN':
    case 'zh':
      return KB_CONTENT_ZH;
    case 'en':
    default:
      return KB_CONTENT_EN;
  }
}
